/**
 * The Data & API contact form's rules (/data): what it asks, how each answer
 * is checked, and the reply-by date its sent state and the confirmation email
 * give. Shared by the browser bundle, the dashboard's Data & API page
 * (web/src/data/LeadCard.tsx) and the server (POST /api/leads/data, the
 * no-JavaScript POST /data and POST /api/leads/data/dashboard), so all of
 * them check a lead the same way and answer in the same words. No Node or
 * DOM APIs in here.
 */

/** The "What you need" chips, in the order the form shows them. */
export const NEEDS = [
  'REST API',
  'Webhooks',
  'Historical data',
  'Bulk export',
  'Custom coverage',
  'Redistribution licence',
] as const;

export type Need = (typeof NEEDS)[number];

/**
 * Personal mailbox providers. An address on one gets a hint asking for a work
 * address, and the lead is flagged `is_free_email`; it is never refused.
 */
export const FREE_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'proton.me',
  'protonmail.com',
  'aol.com',
  'live.com',
];

export const LEAD_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The longest address a mailbox can have (RFC 5321). */
const EMAIL_MAX = 254;
/** Name and company: the inputs stop here, and the server cuts there too. */
export const LINE_MAX = 200;
export const MESSAGE_MAX = 4_000;

export type LeadField = 'name' | 'company' | 'email' | 'message';
export type LeadErrors = Partial<Record<LeadField, string>>;

export const LEAD_ERRORS: Record<LeadField, string> = {
  name: 'Add your name.',
  company: 'Add your company.',
  email: 'Enter a valid email address.',
  // The textarea stops at MESSAGE_MAX, so only a hand-made request sees this.
  message: 'Keep this under 4,000 characters.',
};

export const FREE_EMAIL_HINT =
  'A work address helps us set up seats for your company.';

export interface Lead {
  name: string;
  company: string;
  email: string;
  /** In NEEDS order, whatever order they were picked in. */
  needs: Need[];
  /** Null when left empty. */
  message: string | null;
}

/** One line of text: trimmed, every run of whitespace or control characters one space. */
function line(value: unknown): string {
  return typeof value === 'string'
    ? value.replace(/[\p{Cc}\s]+/gu, ' ').trim()
    : '';
}

/** The free-text answer: trimmed, line breaks kept, other control characters gone. */
function paragraph(value: unknown): string {
  return typeof value === 'string'
    ? value
        .replace(/\r\n?/g, '\n')
        .replace(/[^\P{Cc}\n\t]/gu, '')
        .trim()
    : '';
}

/**
 * Picked chips, in NEEDS order. Anything that is not a chip is dropped: the
 * form cannot send one, so it came from somewhere else.
 */
export function pickNeeds(value: unknown): Need[] {
  const list = Array.isArray(value) ? value : [value];
  return NEEDS.filter((n) => list.includes(n));
}

/**
 * What a lead asks, without who is asking: the whole form on the dashboard's
 * Data & API page, where the account gives the name and the address.
 */
export type LeadDetails = Pick<Lead, 'company' | 'needs' | 'message'>;

/** The company, the chips and the message, cleaned and checked. */
export function checkDetails(raw: {
  company?: unknown;
  needs?: unknown;
  message?: unknown;
}): { details: LeadDetails; errors: LeadErrors } {
  const details: LeadDetails = {
    company: line(raw.company).slice(0, LINE_MAX),
    needs: pickNeeds(raw.needs),
    message: paragraph(raw.message) || null,
  };
  const errors: LeadErrors = {};
  if (!details.company) errors.company = LEAD_ERRORS.company;
  if (details.message && details.message.length > MESSAGE_MAX) {
    errors.message = LEAD_ERRORS.message;
  }
  return { details, errors };
}

/** The form's answers, cleaned, and a message for each one that will not do. */
export function checkLead(raw: {
  name?: unknown;
  company?: unknown;
  email?: unknown;
  needs?: unknown;
  message?: unknown;
}): { lead: Lead; errors: LeadErrors } {
  const { details, errors: detailErrors } = checkDetails(raw);
  const lead: Lead = {
    name: line(raw.name).slice(0, LINE_MAX),
    email: line(raw.email),
    ...details,
  };
  // In the form's order, so the first one is the first field to fix.
  const errors: LeadErrors = {};
  if (!lead.name) errors.name = LEAD_ERRORS.name;
  if (detailErrors.company) errors.company = detailErrors.company;
  if (lead.email.length > EMAIL_MAX || !LEAD_EMAIL.test(lead.email)) {
    errors.email = LEAD_ERRORS.email;
  }
  if (detailErrors.message) errors.message = detailErrors.message;
  return { lead, errors };
}

export function hasErrors(errors: LeadErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** True for an address at a personal provider (FREE_EMAIL_DOMAINS). */
export function isFreeEmail(email: string): boolean {
  const at = email.trim().lastIndexOf('@');
  if (at < 0) return false;
  return FREE_EMAIL_DOMAINS.includes(
    email
      .trim()
      .slice(at + 1)
      .toLowerCase(),
  );
}

/** "Jordan" for "Jordan Ellis": the first word of the name as typed. */
export function firstName(name: string): string {
  return line(name).split(' ')[0] ?? '';
}

// Spelled out rather than taken from Intl, as in format.ts: current CLDR
// abbreviates September in en-GB as "Sept".
const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');

/**
 * The working day after the one `now` falls on in `timeZone` (the runtime's
 * own zone when undefined, which in the browser is the visitor's): tomorrow,
 * or Monday from a Friday, Saturday or Sunday. Public holidays are not known.
 * Returned as noon UTC on that calendar day.
 */
export function nextBusinessDay(now: Date, timeZone?: string): Date {
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now)) {
    p[part.type] = part.value;
  }
  const day = new Date(
    Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 12),
  );
  do {
    day.setUTCDate(day.getUTCDate() + 1);
  } while (day.getUTCDay() === 0 || day.getUTCDay() === 6);
  return day;
}

/** "Wednesday 7 Oct, end of day": when the data team replies by. */
export function replyBy(now: Date, timeZone?: string): string {
  const day = nextBusinessDay(now, timeZone);
  return `${WEEKDAYS[day.getUTCDay()]} ${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]}, end of day`;
}

/** Where a lead came from, as the page saw it when it loaded. */
export interface LeadSource {
  url: string | null;
  referrer: string | null;
  /** utm_source, utm_campaign…, as the page URL carried them. */
  utm: Record<string, string>;
}

const URL_MAX = 2_000;
const UTM_KEY = /^utm_[a-z0-9_]{1,40}$/;
const UTM_MAX = 10;
const UTM_VALUE_MAX = 200;

function urlish(value: unknown): string | null {
  const text = line(value);
  return /^https?:\/\//i.test(text) ? text.slice(0, URL_MAX) : null;
}

/** What the page sends as `source`, kept to the shape and sizes above. */
export function checkSource(value: unknown): LeadSource {
  const raw = (value && typeof value === 'object' ? value : {}) as Record<
    string,
    unknown
  >;
  const utm: Record<string, string> = {};
  const given = (
    raw.utm && typeof raw.utm === 'object' ? raw.utm : {}
  ) as Record<string, unknown>;
  for (const [key, v] of Object.entries(given)) {
    if (Object.keys(utm).length >= UTM_MAX) break;
    const text = line(v).slice(0, UTM_VALUE_MAX);
    if (UTM_KEY.test(key) && text) utm[key] = text;
  }
  return { url: urlish(raw.url), referrer: urlish(raw.referrer), utm };
}

/** The page's own address, its referrer and its utm_* parameters. */
export function sourceOf(href: string, referrer: string): LeadSource {
  const utm: Record<string, string> = {};
  try {
    for (const [key, value] of new URL(href).searchParams) {
      if (key.startsWith('utm_')) utm[key] = value;
    }
  } catch {
    /* not a URL: no parameters */
  }
  return checkSource({ url: href, referrer, utm });
}
